import React, {useCallback, useEffect, useMemo, useState} from "react";
import FastConnectionSearchSection from "../../../../../components/search-sections/fast-connection-search-section";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {FastConnectionDataGridCellKeys} from "../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import LazyImage from "../../../../../components/app-specific/lazy-image";
import ConfigurableInfoBlock, {ConfigurableInfoBlockFeedbackIconPositions, ConfigurableInfoBlockInputTypes} from "../../../../../components/app-specific/configurable-info-block";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../components/base/chips/chips";
import TagChipsContent from "../../../../../components/app-specific/chips-contents/tag-chips-content";
import PartnerDataGrid from "../../../../../containers/data-grid";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import ProductInformationPopover from "../../../../../components/app-specific/draggable-popover/product-information-popover";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import PartAutocomplete from "../../../../../components/text-fields/autocomplete/part-autocomplete";
import {Box} from "@mui/material";

const dataGridSaveConfig = {
    name: 'FastConnectionViewDataGrid',
    version: 1,
}

const FastConnectionView = () => {
    const [data, setData] = useState([]);
    const [relations, setRelations] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('product-fast-connections-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.productsFastConnection);
        configureBehavioralTool((tags) => tags.productsFastConnection);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Memo version of data grid columns
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Part Number",
                name: FastConnectionDataGridCellKeys.partNumber,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                reorderable: false,
                width: 250,
            },
            // {
            //     title: "AM Numbers",
            //     name: FastConnectionDataGridCellKeys.amNumber,
            //     alignment: DataGridColumnAlignments.center,
            //     type: DataGridColumnTypes.element,
            //     sortable: false,
            //     width: 600,
            // },
            // {
            //     title: "OE Numbers",
            //     name: FastConnectionDataGridCellKeys.oeNumber,
            //     alignment: DataGridColumnAlignments.center,
            //     type: DataGridColumnTypes.element,
            //     sortable: false,
            //     width: 600,
            // },
            {
                title: "Tags",
                name: FastConnectionDataGridCellKeys.tags,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 400,
            },
            ...(relations?.map(e => ({
                title: e.title,
                name: e.title,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 400,
                savable: false,
            })) ?? [])
        ], [relations])

    const [dataGridColumns, , dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Searches over parts of the system:
     * - Calls the api for getting the data list of parts from server
     * - if the api call was successful, Sets the data in the state with received values
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (args) => {
        const forApi = {
            keyword: args.filters?.keyword,
            categoryId: args.filters?.category,
            pagination: args.paginationInfo,
        };
        const response = await Api.getFastConnectionData(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const productAllRelationsTypes = (response?.data?.items?.map(part => part.relations) ?? []).flat().map(relation => relation.type);
            const uniqueRelationIds = Array.from(new Set(productAllRelationsTypes?.map(relationType => relationType.id)).values());
            setRelations(uniqueRelationIds.map(relationId => ({
                id: relationId,
                title: productAllRelationsTypes.find(t => t.id === relationId)?.title ?? '',
            })))
            setData(response?.data?.items?.map(part => {
                // const amCodes = part.codes?.filter(code => code?.isAm) ?? [];
                // const oeCodes = part.codes?.filter(code => !code?.isAm) ?? [];
                return {
                    ...part,
                    partNumber: part.partNo,
                    partImageUrl: response?.configuration?.PartImageURL?.concat(part.coverImageFileName) ?? '',
                    rootCategory: part.parentCategory?.title ?? '',
                    mainCategory: part.category?.title ?? '',
                    // amCodes: Array.from(new Set(amCodes?.map(amCode => amCode.code) ?? []).values()).map(code => amCodes.find(d => d.code === code)),
                    // oeNumbers: Array.from(new Set(oeCodes?.map(oeCode => oeCode.code) ?? []).values()).map(code => oeCodes.find(d => d.code === code)),
                    tags: part.tags?.map(tag => ({
                        ...tag,
                        iconFileName: response?.configuration?.TagIconUrlBaseAddress?.concat(tag.iconFileName) ?? '',
                    })),
                    partRelations: uniqueRelationIds.map(relationId => ({
                        id: relationId,
                        title: productAllRelationsTypes.find(t => t.id === relationId)?.title ?? '',
                        connectedParts: part.relations
                            ?.filter(f => f?.type?.id === relationId)
                            ?.map(connectedPart => ({
                                id: connectedPart.connectedPartId,
                                partNumber: connectedPart.connectedPartNumber,
                                connectedPartId: connectedPart.connectedPartId,
                            })) ?? [],
                    }))
                }
            }) ?? [])
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.totalCount ?? 0,
            }))
        }
    }, [isMounted])

    const initialPaginationInfo = useMemo(() => ({
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize ?? DefaultPaginationInfo.pageSize,
        sizes: [5, 10, 15],
    }), [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
    }), [initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Handles adding tag to a part:
     * - Calls the api regarding this action
     * @param tag
     * @param productId
     */
    const addPartTag = async (tag, productId) => {
        const forApi = {
            partId: productId,
            tagId: tag?.id
        }
        const response = await Api.addTagToOperatorPart(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === productId ?
                {
                    ...e,
                    tags: [...e.tags, response?.data]
                } : e
            ))
        }
    }

    /**
     * Handles removing the tag from a part:
     * - Calls the api regarding this action
     * @param tag
     * @param productId
     */
    const removePartTag = async (tag, productId) => {
        const forApi = {
            partId: productId,
            tagId: tag?.id
        }
        const response = await Api.removeTagFromOperatorPart(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === productId ? {
                ...e,
                tags: e.tags.filter(t => t.id !== tag.id)
            } : e))
        }
    }

    /**
     * Adds a new relation to the product
     * @param product
     * @param partId
     * @param relationId
     * @return {Promise<void>}
     */
    const addNewProductToPartRelation = async (product, partId, relationId) => {
        const forApi = {
            currentPartId: partId,
            relatedPartId: product.id,
            relationTypeId: relationId,
        }
        const response = await Api.createOperatorPartRelation(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === partId ? {
                ...e,
                partRelations: e?.partRelations.map(relation => relation.id === relationId
                    ? {
                        ...relation,
                        connectedParts: [...(relation.connectedParts ?? []), {...product, connectedPartId: product.id}]
                    }
                    : relation)
            } : e))
        }
    }

    /**
     * Filters the auto complete options to avoid showing the products already added to the list
     * @return {*}
     */
    const filterAutoCompleteOptions = (options, relation, product) => {
        const existingConnectedPartIds = relation?.connectedParts?.map(connectedPart => connectedPart.connectedPartId) ?? [];
        return options.filter(e => !existingConnectedPartIds.includes(e.id) && e.id !== product.id);
    }

    /**
     * Removes product from a relation of the part
     * @param {object} connectedPart the product selected from the related products
     * @param {number} productId the id of the part
     * @param {number} relationId the id of the relation
     */
    const removePartFromRelation = async (connectedPart, productId, relationId) => {
        const forApi = {
            currentPartId: productId,
            relatedPartId: connectedPart.connectedPartId,
            relationTypeId: relationId,
        }
        const response = await Api.removesOperatorPartRelation(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === productId
                ? {
                    ...e,
                    partRelations: e?.partRelations?.map(partRelation => partRelation.id === relationId
                        ? {
                            ...partRelation,
                            connectedParts: partRelation.connectedParts.filter(t => t.id !== forApi.relatedPartId),
                        }
                        : partRelation
                    )
                }
                : e
            ))
        }
    }

    const getProductRelation = (product, relationId) => {
        return product?.partRelations?.find(partRelation => partRelation.id === relationId);
    }

    /**
     * Memo version of data grid rows
     * @type {DataGridRow[]}
     */
    const rows = useMemo(() => {
        return data?.map((product) => ({
            key: product.id,
            data: product,
            cells: {
                [FastConnectionDataGridCellKeys.partNumber]:
                    <div className={'item-selector part-information'}>
                        <div className={'w-100 d-flex justify-content-center align-items-center'}>
                            <div className={'part-cover-image'}>
                                <LazyImage
                                    src={product.partImageUrl}
                                    alt={product.partNumber}
                                    height={'100%'}
                                    onContextMenu={(e) => e.preventDefault()}
                                    onDragStart={(e) => e.preventDefault()}
                                    className={'disable-downloading-image'}
                                />
                            </div>
                        </div>
                        <ProductInformationPopover partNo={product.partNumber}
                                                   wrapperProps={{className: 'part-number-text-button'}}>
                            {product.partNumber ?? '--'}
                        </ProductInformationPopover>
                        <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5}}>
                            <p {...dataGridCellTextProps} className={'root-category'}>
                                {product.rootCategory}
                            </p>
                            {
                                product.rootCategory && product.mainCategory &&
                                <span {...dataGridCellTextProps} className={'separator'}>{">"}</span>
                            }
                            <p  {...dataGridCellTextProps} className={'main-category'}>
                                {product.mainCategory}
                            </p>
                        </Box>

                    </div>,
                [FastConnectionDataGridCellKeys.tags]:
                    <>
                        <div className={'item-selector p-3'}>
                            <ConfigurableInfoBlock
                                title={''}
                                className={'h-100 mb-2'}
                                showHistoryIcon={false}
                                inputType={ConfigurableInfoBlockInputTypes.tagsAutoComplete}
                                feedbackIconPosition={ConfigurableInfoBlockFeedbackIconPositions.withInput}
                                onHistoryIconClicked={() => false}
                                onValueChanged={(value) => addPartTag(value, product.id)}
                                inputProps={{
                                    placeholder: 'New tag',
                                    removeOnCommit: true,
                                    clearOnBlur: true,
                                    selectedTags: product?.tags ?? [],
                                    label: "Tags",
                                    industryId: product?.industryId,
                                }}
                            />
                            <div className={'flex-fill mb-2'}>
                                <div className={'d-flex flex-wrap chips-container'}>
                                    {
                                        product?.tags?.map(tag => (
                                            <span {...dataGridCellTextProps} key={tag?.id} className={'mr-2 mt-2'}>
                                            <Chips
                                                contentType={ChipsContentType.element}
                                                type={ChipsTypes.remove}
                                                iconPosition={ChipsIconPosition.start}
                                                disabled={false}
                                                onIconClick={() => removePartTag(tag, product.id)}
                                                buttonProps={{size: 'small'}}
                                                content={<TagChipsContent tag={tag} useRepeatCount={false} useIcon
                                                                          industryId={product?.industryId}/>}
                                            />
                                            </span>
                                        ))
                                    }
                                </div>
                            </div>
                        </div>
                    </>,
                ...(Object.fromEntries(relations?.map(relation => {
                    const _relation = getProductRelation(product, relation.id);
                    return [
                        relation.title,
                        <>
                            <div className={'item-selector p-3'}>
                                <PartAutocomplete
                                    label={`${_relation?.title ?? ''}`}
                                    disableCloseOnSelect
                                    value={null}
                                    onChange={(_, value) => addNewProductToPartRelation(value, product.id, _relation.id)}
                                    filterOptions={(options) => filterAutoCompleteOptions(options, _relation, product)}
                                />
                                <div className={'d-flex flex-wrap mt-2'}>
                                    {
                                        _relation?.connectedParts?.map(connectedPart => (
                                            <span {...dataGridCellTextProps} key={connectedPart.id}
                                                  className={'mr-2 mb-2'}>
                                                <Chips
                                                    iconPosition={ChipsIconPosition.start}
                                                    contentType={ChipsContentType.element}
                                                    content={
                                                        <ProductInformationPopover
                                                            partNo={connectedPart?.partNo ?? connectedPart?.partNumber}>
                                                            {connectedPart?.partNo ?? connectedPart?.partNumber ?? ''}
                                                        </ProductInformationPopover>
                                                    }
                                                    onIconClick={() => removePartFromRelation(connectedPart, product.id, _relation.id)}
                                                    type={ChipsTypes.remove}
                                                />
                                            </span>
                                        )) ?? <></>
                                    }
                                </div>
                            </div>
                        </>
                    ]
                })) ?? [])
            }
        })) ?? []
    }, [data, relations])


    return (
        <>
            <div
                className={'d-flex flex-row justify-content-between align-items-center fast-connection-view w-100 px-2 mt-3 mb-2'}>
                <p className={'view-title'}>
                    Fast Connection
                </p>
            </div>
            <div className={'mb-5'}>
                <FastConnectionSearchSection searchData={searchData}/>
            </div>
            <div
                className='fast-connection-view'>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    classNames={{
                        cell: 'fast-connection-table-cell',
                        row: 'fast-connection-row',
                    }}
                    visibleRows={2}
                    rows={rows}
                    state={{
                        pagination: searchData.paginationInfo,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                    }}
                    hideFooterActions
                    hideExporting
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
        </>
    );
}

export default FastConnectionView;
